import { ChangeEvent, FC, memo, MouseEventHandler, useCallback, useMemo, useRef } from "react";
import { Box, FormControlLabel, Paper, Radio, RadioGroup, SxProps, useTheme, Theme } from "@mui/material";
import getStyles from "./styles";

type Props = {
  name: string;
  value: string | number;
  options: Array<{
    label: string;
    value: string | number;
    img?: string;
  }>;
  onSelect?: (value: string | number) => void;
  sx?: SxProps<Theme>;
};

const RadioCard: FC<Props> = ({ name, value: selected, options = [], onSelect, sx = {} }) => {
  const refs = useRef<HTMLLabelElement[]>([]);
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const handleSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>, value: string | number) => {
      event.preventDefault();
      onSelect?.(value);
    },
    [onSelect]
  );

  const handleImageClick: MouseEventHandler<HTMLImageElement> = useCallback((event) => {
    const index = Number(event.currentTarget.dataset.index);
    refs.current[index]?.click();
  }, []);

  return (
    <RadioGroup row name={name} value={selected} onChange={handleSelect} sx={{ ...styles.group, ...sx } as SxProps<Theme>} className="RlxRadioCard">
      {options.map(({ label, value, img }, index) => (
        <Paper key={value} sx={styles.card}>
          <Box component="img" src={img} alt={label} sx={styles.img} data-index={index} onClick={handleImageClick} />
          <FormControlLabel
            ref={(el) => (refs.current[index] = el as HTMLLabelElement)}
            control={<Radio />}
            value={value}
            label={label}
            sx={{
              ...styles.control,
              ...(value === selected ? styles.control.selected : {}),
            }}
          />
        </Paper>
      ))}
    </RadioGroup>
  );
};

RadioCard.displayName = "RadioCard";

export default memo(RadioCard);
